<template>
	<div class="brick">
		<div class="ai_disclaimer" style="margin: 4px 0 10px; padding: 8px 12px; border: 1px solid #f0c36d; background: #fcf8e3; color: #8a6d3b; font-size: 12px; border-radius: 3px;">
			<strong>Note:</strong> These results are generated by AI interpreting your question and may be inaccurate or incomplete. Always verify important figures against the official reports before relying on them.
		</div>

		<!-- Conversation thread -->
		<div class="nlq_thread" ref="thread">
			<!-- Empty state: intro + starter examples -->
			<div v-if="turns.length === 0" class="nlq_welcome">
				<div class="nlq_welcome_title">Ask about your sales, inventory, receivables, and orders</div>
				<div class="nlq_welcome_sub">Click an example below, or type your own question in the box.</div>
			</div>

			<div v-for="(t, i) in turns" :key="i" class="nlq_turn">
				<div class="nlq_q">
					<span class="nlq_q_bubble">{{ t.question }}</span>
				</div>

				<div class="nlq_a">
					<div v-if="t.loading" class="nlq_thinking">Thinking…</div>

					<div v-else-if="t.source === 'notice' || t.source === 'out_of_scope'" class="nlq_notice">{{ t.summary }}</div>

					<template v-else>
						<div v-if="t.answer" class="nlq_answer">{{ t.answer }}</div>
						<div class="nlq_a_head">
							<strong v-if="!t.answer">{{ t.summary }}</strong>
							<span v-else class="lt_text">{{ t.summary }}</span>
							<span v-if="t.source === 'llm' || t.source === 'template'" class="lt_text nlq_tag">[{{ t.source === 'llm' ? 'AI' : 'Quick' }}]</span>
							<span v-if="t.rowCount" class="lt_text nlq_tag">{{ t.rowCount }} row(s)</span>
							<span v-if="t.chartSpec" class="nlq_toggle">
								<a href="javascript:;" :class="t.viewMode === 'chart' ? 'search_bt' : 'gray_bt'" @click="t.viewMode = 'chart'">Chart</a>
								<a href="javascript:;" :class="t.viewMode === 'table' ? 'search_bt' : 'gray_bt'" @click="t.viewMode = 'table'">Table</a>
							</span>
						</div>
						<div v-if="t.notice" class="nlq_meta">{{ t.notice }}</div>

						<div v-if="t.chartSpec && t.viewMode === 'chart'" style="margin: 6px 0 10px;">
							<NlqChart :spec="t.chartSpec" />
						</div>

						<div style="width: 100%; overflow-x: auto;" v-if="t.rows.length && (t.viewMode === 'table' || !t.chartSpec)">
							<table class="list_tbl">
								<thead>
									<tr>
										<th v-for="c in t.columns" :key="c">{{ c }}</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(row, r) in t.rows" :key="r">
										<td v-for="c in t.columns" :key="c">{{ row[c] }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</template>
				</div>
			</div>
		</div>

		<!-- Example / follow-up question chips -->
		<div class="nlq_suggests" v-if="suggestions.length">
			<em class="lt_text">{{ turns.length ? 'Follow-up' : 'Try asking' }}</em>
			<a href="javascript:;" class="gray_bt nlq_chip" v-for="s in suggestions" :key="s" @click="askQuestion(s)">{{ s }}</a>
		</div>

		<!-- Multi-line input -->
		<div class="nlq_inputbar">
			<textarea
				class="nlq_textarea"
				v-model="question"
				rows="2"
				placeholder="Ask a question, e.g. this month's sales, top 10 items by sales, customers overdue 90+ days …  (Enter to send · Shift+Enter for a new line)"
				@keydown.enter.exact.prevent="submit"
			></textarea>
			<div class="nlq_inputbtns">
				<a href="javascript:;" class="search_bt" @click="submit">Ask</a>
				<a href="javascript:;" class="gray_bt" @click="reset">Reset</a>
			</div>
		</div>
	</div>
</template>

<script>
import { apiNlQuery } from '@/api/report/report.js';
import { buildChartSpec } from '@/utils/nlqChart.js';
import NlqChart from './NlqChart.vue';

// Starter examples (spread across domains)
const STARTERS = ["This month's sales", 'Top 10 items by sales', 'Monthly sales trend (last 6 months)', 'Low stock items', 'Total outstanding receivables', 'How many open orders?'];

// Follow-up question pools by domain (offered based on the previous question's domain)
const FOLLOWUPS = {
	sales: ['Monthly sales trend (last 6 months)', 'Top 10 items by sales', 'Sales by sales group', 'Top 5 customers by sales'],
	inventory: ['Stock value by group', 'Low stock items', 'Top items by stock value'],
	ar: ['Customers overdue more than 90 days', 'Receivables by aging bucket', 'Receivables by salesman', 'Total outstanding receivables'],
	orders: ['Open order quantity by customer', 'Orders on hold', 'How many open orders?'],
};

// Detect the domain from the question (accepts Korean or English input)
function detectDomain(q) {
	const s = (q || '').toLowerCase();
	if (/(미수|연체|미납|receivable|overdue|aging|잔액|balance)/.test(s)) return 'ar';
	if (/(미출고|주문|order|hold|백오더|backorder)/.test(s)) return 'orders';
	if (/(재고|stock|inventory)/.test(s)) return 'inventory';
	return 'sales';
}

export default {
	components: { NlqChart },
	data: () => ({
		question: '',
		turns: [],
		suggestions: STARTERS.slice(0, 6),
	}),
	methods: {
		submit() {
			const q = (this.question || '').trim();
			if (!q) {
				this.$toast.error('Please enter a question.');
				return;
			}
			this.askQuestion(q);
		},
		async askQuestion(q) {
			this.question = '';
			// Conversation context: last 3 answered turns (question + generated SQL) so follow-ups work.
			const history = this.turns
				.filter(t => !t.loading && t.sql && (t.source === 'llm' || t.source === 'template'))
				.slice(-3)
				.map(t => ({ question: t.question, sql: t.sql }));
			const turn = { question: q, loading: true, error: null, summary: '', source: '', columns: [], rows: [], rowCount: 0, truncated: false, chartSpec: null, viewMode: 'table', answer: null, sql: null, notice: null };
			this.turns.push(turn);
			this.scrollDown();
			try {
				const { data } = await apiNlQuery({ question: q, context: 'report', history });
				turn.summary = data.summary;
				turn.source = data.source;
				turn.answer = data.answer || null;
				turn.sql = data.sql || null;
				// Server-applied limits (detail date window, summary as-of/coverage) shown under the answer.
				turn.notice = data.notice || null;
				turn.columns = data.columns || [];
				turn.rows = data.rows || [];
				turn.rowCount = data.rowCount || 0;
				turn.truncated = data.truncated;
				turn.chartSpec = buildChartSpec(turn.columns, turn.rows);
				turn.viewMode = turn.chartSpec ? 'chart' : 'table';
				if (turn.truncated) this.$toast.warning('Result was truncated (row limit reached).');
			} catch (e) {
				const body = e && e.response && e.response.data;
				const msg = (body && (body.message || (typeof body === 'string' ? body : null))) || 'Sorry, something went wrong. Please try again in a moment.';
				turn.summary = typeof msg === 'string' ? msg : 'Sorry, something went wrong. Please try again in a moment.';
				turn.source = 'notice';
			} finally {
				turn.loading = false;
				this.suggestions = this.buildSuggestions(q);
				this.scrollDown();
			}
		},
		buildSuggestions(q) {
			const pool = FOLLOWUPS[detectDomain(q)] || FOLLOWUPS.sales;
			const norm = s => s.replace(/\s+/g, '').toLowerCase();
			const asked = norm(q);
			let list = pool.filter(s => norm(s) !== asked);
			// If fewer than 3, top up from the starter set
			if (list.length < 3) {
				for (const s of STARTERS) {
					if (list.length >= 3) break;
					if (norm(s) !== asked && !list.some(x => norm(x) === norm(s))) list.push(s);
				}
			}
			return list.slice(0, 3);
		},
		scrollDown() {
			this.$nextTick(() => {
				const el = this.$refs.thread;
				if (el) el.scrollTop = el.scrollHeight;
			});
		},
		reset() {
			this.question = '';
			this.turns = [];
			this.suggestions = STARTERS.slice(0, 6);
		},
	},
	created() {
		this.$store.commit('setSubTitle', 'Report -> AI Query');
		this.$store.commit('setSelectedMenu', 'nav_li_nlquery');
	},
};
</script>

<style scoped>
.nlq_thread {
	max-height: 58vh;
	overflow-y: auto;
	border: 1px solid #e2e6ea;
	border-radius: 6px;
	background: #fafbfc;
	padding: 14px;
	margin-bottom: 10px;
}
.nlq_welcome {
	text-align: center;
	color: #8a949e;
	padding: 40px 10px;
}
.nlq_welcome_title {
	font-size: 16px;
	font-weight: 600;
	color: #5a646e;
	margin-bottom: 6px;
}
.nlq_welcome_sub {
	font-size: 13px;
}
.nlq_turn {
	margin-bottom: 18px;
}
.nlq_q {
	text-align: right;
	margin-bottom: 8px;
}
.nlq_q_bubble {
	display: inline-block;
	background: #2f6fed;
	color: #fff;
	padding: 7px 13px;
	border-radius: 14px 14px 2px 14px;
	font-size: 14px;
	max-width: 80%;
	text-align: left;
	white-space: pre-wrap;
}
.nlq_a {
	background: #fff;
	border: 1px solid #e2e6ea;
	border-radius: 4px 14px 14px 14px;
	padding: 12px 14px;
}
.nlq_a_head {
	margin-bottom: 6px;
}
.nlq_tag {
	margin-left: 8px;
}
.nlq_toggle {
	margin-left: 12px;
}
.nlq_thinking {
	color: #8a949e;
	font-size: 13px;
}
.nlq_answer {
	font-size: 14px;
	font-weight: 600;
	color: #2c3540;
	line-height: 1.55;
	margin-bottom: 7px;
	white-space: pre-wrap;
}
.nlq_meta {
	color: #6b7680;
	font-size: 12px;
	line-height: 1.5;
	margin: 2px 0 8px;
	white-space: pre-wrap;
}
.nlq_notice {
	color: #4a5560;
	font-size: 13px;
	line-height: 1.5;
	background: #eef4ff;
	border: 1px solid #d6e2f7;
	border-radius: 4px;
	padding: 9px 12px;
}
.nlq_suggests {
	margin-bottom: 8px;
}
.nlq_chip {
	margin: 3px 4px 3px 0;
}
.nlq_inputbar {
	display: flex;
	gap: 8px;
	align-items: flex-start;
}
.nlq_textarea {
	flex: 1;
	resize: vertical;
	min-height: 44px;
	padding: 8px 10px;
	border: 1px solid #c5ccd3;
	border-radius: 4px;
	font-family: inherit;
	font-size: 14px;
	line-height: 1.5;
}
.nlq_inputbtns {
	display: flex;
	flex-direction: column;
	gap: 6px;
	white-space: nowrap;
}
</style>
